// src/pages/ChatBot/DocumentRechunkModal.tsx
//
// Re-processes ONE uploaded chatbot document under a chosen chunking config,
// opened from the Inspect modal's "Rechunk" button. A strategy select at the top
// (structure / fixed / semantic) chooses the code path; below it sit ONLY the
// selected strategy's knobs, prefilled with the document's EFFECTIVE values (its
// stored per-document override merged over the org's active chunking) and freely
// editable within the registry ranges. Submitting POSTs
// /chatbot/documents/rechunk, which re-chunks and re-embeds the document now: the
// sent values persist as a per-document override when they differ from the org
// config, or clear it when they match. On success the parent refetches details.

import { useState } from 'react';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { useModalBackdropClose } from '../../hooks/useModalBackdropClose';
import './DocumentRechunkModal.css';

export type ChunkingStrategy = 'structure' | 'fixed' | 'semantic';

// The org's active ingress chunking is the FULL shape: a strategy plus every
// strategy's knobs. It's the prefill baseline for whichever strategy is chosen.
export interface StructureKnobs {
  minChunkTokens: number;
  maxChunkTokens: number;
  overlapRatio: number;
}
export interface FixedKnobs {
  maxTokens: number;
  overlapRatio: number;
}
export interface SemanticKnobs {
  maxTokens: number;
  breakpointPercentile: number;
  minSentencesPerChunk: number;
}

export interface ChunkingValues {
  strategy: ChunkingStrategy;
  structure: StructureKnobs;
  fixed: FixedKnobs;
  semantic: SemanticKnobs;
}

// A stored per-document override: a strategy plus whichever branch knobs were
// set. Branch knobs are optional (only the set ones ride along), so the prefill
// and the tooltip both merge over / read against the active config defensively.
export interface ChunkingOverride {
  strategy?: ChunkingStrategy;
  structure?: Partial<StructureKnobs>;
  fixed?: Partial<FixedKnobs>;
  semantic?: Partial<SemanticKnobs>;
}

interface KnobDef {
  key: string;
  label: string;
  unit: string;
  min: number;
  max: number;
  step: number;
  decimals: number; // range-hint formatting (fractional overlap reads cleanly)
}

// Per-strategy knob domains — kept in step with backend config/registry/chunking.ts.
const STRATEGY_KNOBS: Record<ChunkingStrategy, KnobDef[]> = {
  structure: [
    { key: 'maxChunkTokens', label: 'Maximum chunk size', unit: 'tokens', min: 128, max: 2048, step: 32, decimals: 0 },
    { key: 'minChunkTokens', label: 'Minimum chunk size', unit: 'tokens', min: 20, max: 200, step: 10, decimals: 0 },
    { key: 'overlapRatio', label: 'Chunk overlap ratio', unit: '', min: 0, max: 0.5, step: 0.05, decimals: 2 },
  ],
  fixed: [
    { key: 'maxTokens', label: 'Maximum chunk size', unit: 'tokens', min: 128, max: 2048, step: 32, decimals: 0 },
    { key: 'overlapRatio', label: 'Chunk overlap ratio', unit: '', min: 0, max: 0.5, step: 0.05, decimals: 2 },
  ],
  semantic: [
    { key: 'maxTokens', label: 'Maximum chunk size', unit: 'tokens', min: 128, max: 2048, step: 32, decimals: 0 },
    { key: 'breakpointPercentile', label: 'Breakpoint percentile', unit: '', min: 50, max: 99, step: 1, decimals: 0 },
    { key: 'minSentencesPerChunk', label: 'Minimum sentences per chunk', unit: '', min: 1, max: 10, step: 1, decimals: 0 },
  ],
};

const STRATEGY_OPTIONS: { value: ChunkingStrategy; label: string }[] = [
  { value: 'structure', label: 'Structure-aware' },
  { value: 'fixed', label: 'Fixed-size' },
  { value: 'semantic', label: 'Semantic' },
];

interface DocumentRechunkModalProps {
  blobName: string;
  // The org's active ingress chunking — the prefill baseline (full shape).
  activeChunking: ChunkingValues;
  // The document's stored override (if any), merged over activeChunking for the prefill.
  chunkingOverride?: ChunkingOverride;
  onClose: () => void;
  // Called after a successful rechunk so the parent can refetch details + flash a note.
  onRechunked: () => void;
}

// Effective prefill for one knob: the override's set value for it (if the
// override targets this strategy's branch), else the org's active value.
function effectiveKnob(
  strategy: ChunkingStrategy,
  key: string,
  active: ChunkingValues,
  override?: ChunkingOverride,
): number {
  const overrideBranch = override?.[strategy] as Record<string, number | undefined> | undefined;
  const activeBranch = active[strategy] as unknown as Record<string, number>;
  return overrideBranch?.[key] ?? activeBranch[key];
}

// Every strategy's fields prefilled up front (as strings so inputs stay freely
// editable), so switching the select preserves any edits and never loses them.
function initialValues(
  active: ChunkingValues,
  override?: ChunkingOverride,
): Record<ChunkingStrategy, Record<string, string>> {
  const byStrategy = {} as Record<ChunkingStrategy, Record<string, string>>;
  for (const strategy of Object.keys(STRATEGY_KNOBS) as ChunkingStrategy[]) {
    const map: Record<string, string> = {};
    for (const knob of STRATEGY_KNOBS[strategy]) {
      map[knob.key] = String(effectiveKnob(strategy, knob.key, active, override));
    }
    byStrategy[strategy] = map;
  }
  return byStrategy;
}

export default function DocumentRechunkModal({
  blobName,
  activeChunking,
  chunkingOverride,
  onClose,
  onRechunked,
}: DocumentRechunkModalProps) {
  const [strategy, setStrategy] = useState<ChunkingStrategy>(
    chunkingOverride?.strategy ?? activeChunking.strategy,
  );
  const [values, setValues] = useState<Record<ChunkingStrategy, Record<string, string>>>(() =>
    initialValues(activeChunking, chunkingOverride),
  );
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const backdropHandlers = useModalBackdropClose(onClose);
  useEscapeClose(onClose, !isRunning);

  const knobs = STRATEGY_KNOBS[strategy];
  const strategyValues = values[strategy];

  const isKnobValid = (knob: KnobDef, raw: string): boolean => {
    const n = Number(raw);
    return raw.trim() !== '' && !Number.isNaN(n) && n >= knob.min && n <= knob.max;
  };
  // Only the selected strategy's knobs gate Rechunk — the others are never sent.
  const allValid = knobs.every((knob) => isKnobValid(knob, strategyValues[knob.key]));

  // Dirty gate: Rechunk applies a change only when the form differs from the
  // document's CURRENT effective chunking (its stored override merged over the
  // org's active config) — which is exactly what the form is prefilled with. A
  // strategy switch alone counts as a difference; so does any shown knob whose
  // value no longer matches its effective value. A pristine form has nothing to
  // apply, so Rechunk stays disabled.
  const effectiveStrategy = chunkingOverride?.strategy ?? activeChunking.strategy;
  const isKnobDirty = (knob: KnobDef, raw: string): boolean => {
    const effective = effectiveKnob(strategy, knob.key, activeChunking, chunkingOverride);
    const n = Number(raw);
    if (raw.trim() === '' || Number.isNaN(n)) return true; // unparseable ≠ effective
    return n !== effective;
  };
  const isDirty =
    strategy !== effectiveStrategy ||
    knobs.some((knob) => isKnobDirty(knob, strategyValues[knob.key]));

  const setValue = (key: string, raw: string) =>
    setValues((prev) => ({ ...prev, [strategy]: { ...prev[strategy], [key]: raw } }));

  const handleRechunk = async () => {
    if (!allValid || !isDirty || isRunning) return;
    setIsRunning(true);
    setError(null);
    try {
      // Send the strategy plus ONLY that strategy's branch of knob values.
      const knobValues: Record<string, number> = {};
      for (const knob of knobs) knobValues[knob.key] = Number(strategyValues[knob.key]);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/chatbot/documents/rechunk`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          blobName,
          chunking: { strategy, [strategy]: knobValues },
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        let fallback = 'Failed to rechunk document.';
        if (res.status === 409) {
          fallback = 'Documents are being re-processed — try again when the rebuild completes.';
        } else if (res.status === 422) {
          fallback = 'Invalid chunking settings — check the values and try again.';
        }
        throw new Error(body.error || fallback);
      }
      onRechunked();
      onClose();
    } catch (err: unknown) {
      console.error('Failed to rechunk document:', err);
      setError(err instanceof Error ? err.message : 'Failed to rechunk document.');
      setIsRunning(false);
    }
  };

  return (
    <div className="mcrk-modal-overlay" {...backdropHandlers}>
      <div className="mcrk-modal" role="dialog" aria-modal="true" aria-labelledby="mcrk-title">
        <div className="mcrk-modal-header">
          <h2 id="mcrk-title" className="mcrk-modal-title">
            Rechunk document
          </h2>
        </div>

        <div className="mcrk-modal-body">
          <p className="mcrk-note">
            Rechunking re-processes and re-embeds this document now. Values that match your organization's
            settings clear any custom chunking; values that differ are saved for this document.
          </p>

          <div className="mcrk-fields">
            <label className="mcrk-field">
              <span className="mcrk-field-label">Strategy</span>
              <span className="mcrk-field-control">
                <select
                  className="mcrk-input mcrk-select"
                  value={strategy}
                  disabled={isRunning}
                  onChange={(e) => setStrategy(e.target.value as ChunkingStrategy)}
                >
                  {STRATEGY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </span>
            </label>

            {knobs.map((knob) => {
              const invalid = !isKnobValid(knob, strategyValues[knob.key]);
              return (
                <label key={knob.key} className="mcrk-field mcrk-field-nested">
                  <span className="mcrk-field-label">
                    {knob.label}
                    {knob.unit && <span className="mcrk-field-unit"> ({knob.unit})</span>}
                  </span>
                  <span className="mcrk-field-control">
                    <input
                      type="number"
                      className={`mcrk-input${invalid ? ' mcrk-input-invalid' : ''}`}
                      value={strategyValues[knob.key]}
                      min={knob.min}
                      max={knob.max}
                      step={knob.step}
                      disabled={isRunning}
                      onChange={(e) => setValue(knob.key, e.target.value)}
                    />
                    <span className="mcrk-range-hint">
                      {knob.min.toFixed(knob.decimals)}–{knob.max.toFixed(knob.decimals)}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>

          {strategy === 'semantic' && (
            <p className="mcrk-cost-note">
              Semantic chunking embeds every sentence to find topic breakpoints (slower, higher
              embedding cost) and ignores the overlap setting.
            </p>
          )}

          {error && <div className="mcrk-error">{error}</div>}
        </div>

        <div className="mcrk-modal-footer">
          <button type="button" className="mcrk-btn mcrk-btn-ghost" onClick={onClose} disabled={isRunning}>
            Cancel
          </button>
          <button
            type="button"
            className="mcrk-btn mcrk-btn-accent"
            onClick={handleRechunk}
            disabled={!allValid || !isDirty || isRunning}
            title={!isDirty ? 'No changes to apply' : undefined}
          >
            {isRunning && <span className="mcrk-spinner" />}
            {isRunning ? 'Rechunking…' : 'Rechunk'}
          </button>
        </div>
      </div>
    </div>
  );
}
